import { availableVisualAcuityTestConfigurationOptions } from "@constants/testConstants/testSpecificConstants";
import {
  AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR_BACK2BACK,
  AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR,
} from "@constants/testConstants/configurationHelperConstants";
import { AvailablePreTestConfigurationOptions, TestConfiguration, TestType } from "types/testTypes";
import { cloneDeep } from "lodash";
import { SnellenSize } from "../../types/tests/core";

export function GetUpdatedAvailableVisualAcuityConfigsAfterConfigUpdate(
  requestedUpdate: Partial<TestConfiguration>,
  baseOptions?: AvailablePreTestConfigurationOptions | null,
): AvailablePreTestConfigurationOptions {
  let availableVisualAcuityConfig: AvailablePreTestConfigurationOptions = cloneDeep(
    baseOptions ?? availableVisualAcuityTestConfigurationOptions,
  );

  const testType: TestType | undefined = requestedUpdate.testType;

  // For TumblingE, allow Right To Left Eye option (back-to-back)
  // For other test types (Logmar), exclude Right To Left Eye option
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    availableVisualAcuityConfig = {
      ...availableVisualAcuityConfig,
      eyeConfigurationOptions: { ...AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR_BACK2BACK },
    };
  } else {
    availableVisualAcuityConfig = {
      ...availableVisualAcuityConfig,
      eyeConfigurationOptions: { ...AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR },
      // Logmar's headset stage has no tutorial implementation -- hide the
      // toggle rather than let it silently do nothing.
      languageOptions: null,
      testSpecificConfigurationOptions: {
        ...availableVisualAcuityConfig.testSpecificConfigurationOptions,
        toggleShowTutorial: null,
      },
    };
  }

  return availableVisualAcuityConfig;
}

// ─── VA Result Display Helpers ───────────────────────────────────────────────

export enum VaStatus {
  Normal = "normal",
  Borderline = "borderline",
  Attention = "attention",
}

export enum VisualAcuityNotation {
  SnellenDecimal = "snellenDecimal",
  SnellenFeet = "snellenFeet",
  SnellenMeter = "snellenMeter",
  Logmar = "logmar",
}

export interface VisualAcuityDisplayValues {
  snellenDecimal: string;
  snellenFeet: string;
  snellenMeter: string;
  logmar: string;
}

/** LogMAR score → Snellen notation. Extracted from VAcuityData.tsx for shared use. */
export const LOGMAR_TO_SNELLEN: Record<number, string> = {
  0.1: "20/25",
  0.2: "20/30",
  0.3: "20/40",
  0.4: "20/50",
  0.5: "20/63",
  0.55: "20/70",
  0.6: "20/80",
  0.7: "20/100",
  0.8: "20/125",
  0.9: "20/160",
  1: "20/200",
};

export const SNELLEN_SIZE_TO_STRING: Record<SnellenSize, string> = {
  [SnellenSize.SIZE_20_200]: "20/200",
  [SnellenSize.SIZE_20_100]: "20/100",
  [SnellenSize.SIZE_20_80]: "20/80",
  [SnellenSize.SIZE_20_63]: "20/63",
  [SnellenSize.SIZE_20_50]: "20/50",
  [SnellenSize.SIZE_20_40]: "20/40",
  [SnellenSize.SIZE_20_32]: "20/32",
  [SnellenSize.SIZE_20_25]: "20/25",
  [SnellenSize.SIZE_20_20]: "20/20",
};

export const SNELLEN_FEET_TO_DISPLAY_VALUES: Record<string, VisualAcuityDisplayValues> = {
  "20/200": { snellenDecimal: "0.10", snellenFeet: "20/200", snellenMeter: "6/60", logmar: "1.0" },
  "20/160": { snellenDecimal: "0.13", snellenFeet: "20/160", snellenMeter: "6/48", logmar: "0.9" },
  "20/125": { snellenDecimal: "0.16", snellenFeet: "20/125", snellenMeter: "6/38", logmar: "0.8" },
  "20/100": { snellenDecimal: "0.20", snellenFeet: "20/100", snellenMeter: "6/30", logmar: "0.7" },
  "20/80": { snellenDecimal: "0.25", snellenFeet: "20/80", snellenMeter: "6/24", logmar: "0.6" },
  "20/70": { snellenDecimal: "0.29", snellenFeet: "20/70", snellenMeter: "6/21", logmar: "0.55" },
  "20/63": { snellenDecimal: "0.32", snellenFeet: "20/63", snellenMeter: "6/19", logmar: "0.5" },
  "20/50": { snellenDecimal: "0.40", snellenFeet: "20/50", snellenMeter: "6/15", logmar: "0.4" },
  "20/40": { snellenDecimal: "0.50", snellenFeet: "20/40", snellenMeter: "6/12", logmar: "0.3" },
  "20/32": { snellenDecimal: "0.63", snellenFeet: "20/32", snellenMeter: "6/9", logmar: "0.2" },
  "20/30": { snellenDecimal: "0.67", snellenFeet: "20/30", snellenMeter: "6/9", logmar: "0.2" },
  "20/25": { snellenDecimal: "0.80", snellenFeet: "20/25", snellenMeter: "6/7.5", logmar: "0.1" },
  "20/20": { snellenDecimal: "1.00", snellenFeet: "20/20", snellenMeter: "6/6", logmar: "0.0" },
};

/** Maps VaStatus to a Mantine colour token. */
export function getVaStatusColor(status: VaStatus): string {
  switch (status) {
    case VaStatus.Normal:
      return "green";
    case VaStatus.Borderline:
      return "orange";
    case VaStatus.Attention:
      return "red";
  }
}

/** Human-readable label for a VaStatus value. */
export function getVaStatusLabel(status: VaStatus): string {
  switch (status) {
    case VaStatus.Normal:
      return "Normal";
    case VaStatus.Borderline:
      return "Borderline";
    case VaStatus.Attention:
      return "Needs Review";
  }
}

export function getSnellenFromLogmar(logmar: number): string {
  const keys = Object.keys(LOGMAR_TO_SNELLEN).map(Number);
  const nearest = keys.reduce((prev, curr) =>
    Math.abs(curr - logmar) < Math.abs(prev - logmar) ? curr : prev,
  );
  return LOGMAR_TO_SNELLEN[nearest] ?? "N/A";
}

export function getDecimalFromSnellenString(snellen: string | null | undefined): string | null {
  if (!snellen) return null;
  return SNELLEN_FEET_TO_DISPLAY_VALUES[snellen]?.snellenDecimal ?? null;
}

export function getDecimalFromLogmar(logmar: number): string | null {
  return getDecimalFromSnellenString(getSnellenFromLogmar(logmar));
}

export function getSnellenStringFromSize(
  snellenSize: SnellenSize | null | undefined,
): string | null {
  if (!snellenSize) return null;
  return SNELLEN_SIZE_TO_STRING[snellenSize] ?? null;
}

export function getDecimalFromSnellenSize(
  snellenSize: SnellenSize | null | undefined,
): string | null {
  return getDecimalFromSnellenString(getSnellenStringFromSize(snellenSize));
}

export function getDisplayValuesFromSnellenString(
  snellen: string | null | undefined,
): VisualAcuityDisplayValues | null {
  if (!snellen) return null;
  return SNELLEN_FEET_TO_DISPLAY_VALUES[snellen] ?? null;
}

export function getDisplayValuesFromLogmar(logmar: number): VisualAcuityDisplayValues | null {
  return getDisplayValuesFromSnellenString(getSnellenFromLogmar(logmar));
}

export function getDisplayValuesFromSnellenSize(
  snellenSize: SnellenSize | null | undefined,
): VisualAcuityDisplayValues | null {
  return getDisplayValuesFromSnellenString(getSnellenStringFromSize(snellenSize));
}

export function getVisualAcuityLabelByNotation(
  displayValues: VisualAcuityDisplayValues | null | undefined,
  notation: VisualAcuityNotation,
): string {
  if (!displayValues) return "";

  switch (notation) {
    case VisualAcuityNotation.SnellenDecimal:
      return displayValues.snellenDecimal;
    case VisualAcuityNotation.SnellenFeet:
      return displayValues.snellenFeet;
    case VisualAcuityNotation.SnellenMeter:
      return displayValues.snellenMeter;
    case VisualAcuityNotation.Logmar:
      return displayValues.logmar;
  }
}

/**
 * Returns the Snellen acuity string (e.g. "20/40") from a raw test result object.
 * Handles Logmar (finalValue) and TumblingE/TumblingEThreshold (results.thresholdSnellenSize).
 */
export function getVaSnellenString(testResult: any): string | null {
  if (!testResult) return null;
  const testType: string = testResult.testType;
  if (testType === TestType.Logmar) {
    if (typeof testResult.finalValue !== "number") return null;
    const snellen = getSnellenFromLogmar(testResult.finalValue);
    if (isAtOrBetterThan2040(testResult)) return "20/40+";
    return snellen;
  }
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    const snellenSize: SnellenSize | undefined = testResult.results?.thresholdSnellenSize;
    if (!snellenSize) return null;
    if (isAtOrBetterThan2040(testResult)) return "20/40+";
    return SNELLEN_SIZE_TO_STRING[snellenSize] ?? null;
  }
  return null;
}

export function getVaDecimalString(testResult: any): string | null {
  if (!testResult) return null;
  const testType: string = testResult.testType;
  if (testType === TestType.Logmar) {
    if (typeof testResult.finalValue !== "number") return null;
    return getDecimalFromLogmar(testResult.finalValue);
  }
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    return getDecimalFromSnellenSize(testResult.results?.thresholdSnellenSize);
  }
  return null;
}

export function getVaDisplayLabel(testResult: any, notation: VisualAcuityNotation): string | null {
  if (!testResult) return null;
  const testType: string = testResult.testType;

  if (testType === TestType.Logmar) {
    if (typeof testResult.finalValue !== "number") return null;
    return getVisualAcuityLabelByNotation(
      getDisplayValuesFromLogmar(testResult.finalValue),
      notation,
    );
  }

  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    return getVisualAcuityLabelByNotation(
      getDisplayValuesFromSnellenSize(testResult.results?.thresholdSnellenSize),
      notation,
    );
  }

  return null;
}

const SNELLEN_SIZES_AT_OR_BETTER_THAN_2040: SnellenSize[] = [
  SnellenSize.SIZE_20_20,
  SnellenSize.SIZE_20_25,
  SnellenSize.SIZE_20_32,
  SnellenSize.SIZE_20_40,
];

function isAtOrBetterThan2040(testResult: any): boolean {
  const testType: string = testResult.testType;
  if (testType === TestType.Logmar) {
    return typeof testResult.finalValue === "number" && testResult.finalValue <= 0.3;
  }
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    const snellenSize: SnellenSize | undefined = testResult.results?.thresholdSnellenSize;
    return !!snellenSize && SNELLEN_SIZES_AT_OR_BETTER_THAN_2040.includes(snellenSize);
  }
  return false;
}

/**
 * Returns the logMAR value for a VA test result, or null if it can't be determined.
 * Handles Logmar (finalValue) and TumblingE / TumblingEThreshold (results.thresholdSnellenSize).
 */
export function getVaLogMar(testResult: any): number | null {
  if (!testResult) return null;
  const testType: string = testResult.testType;
  if (testType === TestType.Logmar) {
    return typeof testResult.finalValue === "number" ? testResult.finalValue : null;
  }
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    const size: SnellenSize | undefined = testResult.results?.thresholdSnellenSize;
    const snellen = getSnellenStringFromSize(size);
    if (!snellen) return null;
    const raw = SNELLEN_FEET_TO_DISPLAY_VALUES[snellen]?.logmar;
    const parsed = raw != null ? parseFloat(raw) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Returns a 0–100 health score suitable for a ring progress indicator.
 * Higher = better acuity.
 */
export function getVaHealthScore(testResult: any): number {
  if (!testResult) return 0;
  if (isAtOrBetterThan2040(testResult)) return 100;
  const testType: string = testResult.testType;
  if (testType === TestType.Logmar) {
    if (typeof testResult.finalValue !== "number") return 0;
    return Math.round((1 - Math.min(Math.max(testResult.finalValue, 0), 1)) * 100);
  }
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    const snellenSize: SnellenSize | undefined = testResult.results?.thresholdSnellenSize;
    if (!snellenSize) return 0;
    const scores: Record<SnellenSize, number> = {
      [SnellenSize.SIZE_20_20]: 100,
      [SnellenSize.SIZE_20_25]: 100,
      [SnellenSize.SIZE_20_32]: 100,
      [SnellenSize.SIZE_20_40]: 100,
      [SnellenSize.SIZE_20_50]: 55,
      [SnellenSize.SIZE_20_63]: 40,
      [SnellenSize.SIZE_20_80]: 25,
      [SnellenSize.SIZE_20_100]: 15,
      [SnellenSize.SIZE_20_200]: 5,
    };
    return scores[snellenSize] ?? 0;
  }
  return 0;
}

/**
 * Returns VaStatus based on the test result.
 * Logmar: ≤0.2 → Normal, ≤0.5 → Borderline, >0.5 → Attention
 * TumblingE: 20/20–20/32 → Normal, 20/40–20/63 → Borderline, else → Attention
 */
export function getVaStatus(testResult: any): VaStatus {
  if (!testResult) return VaStatus.Attention;
  if (isAtOrBetterThan2040(testResult)) return VaStatus.Normal;
  const testType: string = testResult.testType;
  if (testType === TestType.Logmar) {
    if (typeof testResult.finalValue !== "number") return VaStatus.Attention;
    const logmar = testResult.finalValue;
    if (logmar <= 0.5) return VaStatus.Borderline;
    return VaStatus.Attention;
  }
  if (testType === TestType.TumblingE || testType === TestType.TumblingEThreshold) {
    const snellenSize: SnellenSize | undefined = testResult.results?.thresholdSnellenSize;
    if (!snellenSize) return VaStatus.Attention;
    if (snellenSize === SnellenSize.SIZE_20_50 || snellenSize === SnellenSize.SIZE_20_63) {
      return VaStatus.Borderline;
    }
    return VaStatus.Attention;
  }
  return VaStatus.Attention;
}
